import { ArrowDropDown } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import { CatalogItem } from 'entities/CatalogItem';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { getSelectCategoryId } from 'entities/Category/model/selectors/categorySelectors';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { getSearchCatalogItemIsActive } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { getPropertyCatalogId } from 'features/fetchPropertyById/model/selectors/fetchPropertyByIdSelectors';
import {
    getRouteGlobalCatalog,
    getRouteGlobalCatalogItem,
    getRouteOrderCatalogCategoriesItem,
    getRoutePropertiesCatalogItem,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFeatureFlag } from 'shared/lib/hooks/useFeatureFlag';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { SuppliersList } from 'shared/ui/SuppliersList';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { CatalogItemPrice } from './PropertyCatalogItemPrice';

interface PropertyCatalogItemProps {
    id: string;
    uid?: string;
    idToEdit?: string;
    isInOrder?: boolean;
    isOrderPage?: boolean;
    title: string;
    thumbnailUrl?: string;
    moveTo: PropertyCatalogItemsMoveTo;
    qty: number;
    isActive: boolean;
    sku: string;
    uom: string;
    qtyPerUom: number;
    minAllowedInStock: number;
    quickEdit?: boolean;
    categoryId?: string;
    isSearch?: boolean;
    isInventory: boolean;
    price: number;
    proposals: Proposal[];
    activatedAccounts: { [key: string]: boolean[] };
}

// enum OrderActionType {
//     ADD_ITEM = 'add',
//     DELETE_ITEM = 'delete',
//     EDIT_QTY_ITEM = 'edit',
// }

//TODO: Refactor
// Separate item by catalog items types:
// 1. Global catalog item
// 2. Property catalog item
//  2.1. Catalog item
//  2.2. Item in order

export const PropertyCatalogItem = ({
    id,
    uid,
    idToEdit,
    isInOrder,
    title,
    thumbnailUrl,
    moveTo,
    qty,
    isActive,
    isOrderPage,
    sku,
    uom,
    qtyPerUom,
    minAllowedInStock,
    quickEdit,
    categoryId,
    isInventory,
    price,
    isSearch,
    proposals,
    activatedAccounts,
}: PropertyCatalogItemProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedPropertyCatalogId = useSelector(getPropertyCatalogId);
    const isCatalogItemsSearching = useSelector(getSearchCatalogItemIsActive);
    const catalogPricesVisible = useFeatureFlag('catalog_prices_is_visible');
    const canSeePrices =
        useSelector(getUserCanSeePrices) && catalogPricesVisible;
    const { openLightbox } = useImageLightbox();

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );

    const isMoveToOrderMode = moveTo === PropertyCatalogItemsMoveTo.ORDER;
    const isMoveToCatalogMode = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const isMoveToInventoryMode =
        moveTo === PropertyCatalogItemsMoveTo.INVENTORY;

    const showCategoryLabel = isCatalogItemsSearching;

    const totalPrice =
        qty && (isOrderPage || isMoveToOrderMode) ? qty * price : 0;

    const showCheckbox = !(
        isMoveToCatalogMode &&
        isCatalogItemsSearching &&
        !isGlobalCatalog
    );

    const showPrice =
        (price || price === 0) &&
        canSeePrices &&
        !(isGlobalCatalog && !totalPrice);

    const itemLabel =
        (isMoveToInventoryMode ||
            isMoveToCatalogMode ||
            isMoveToOrderMode ||
            isInOrder) &&
        isInventory
            ? t('Inventory')
            : '';

    const expandDetailedView = useCallback(
        (id: string) => () => {
            dispatch(catalogItemActions.setSelectedItemById(id));
            dispatch(catalogItemActions.setShowDetailedView(true));

            // Don't navigate when in search mode
            if (isSearch) {
                return;
            }

            if (isMoveToCatalogMode) {
                if (isGlobalCatalog) {
                    navigate(
                        getRouteGlobalCatalogItem(
                            selectedPropertyId,
                            selectedCategoryId,
                            id,
                        ),
                    );
                } else {
                    navigate(
                        getRoutePropertiesCatalogItem(
                            selectedPropertyId,
                            selectedCategoryId,
                            id,
                        ),
                    );
                }
            } else {
                // for order view details
                navigate(getRouteOrderCatalogCategoriesItem(id), {
                    state: {
                        selectedOrderId,
                        selectedPropertyCatalogId,
                        selectedPropertyId,
                        fromOrder: selectedOrderId,
                    },
                });
            }
        },
        [
            dispatch,
            isGlobalCatalog,
            isMoveToCatalogMode,
            isSearch,
            navigate,
            selectedCategoryId,
            selectedOrderId,
            selectedPropertyCatalogId,
            selectedPropertyId,
        ],
    );

    return (
        <Box
            data-testclass="item-preview"
            sx={{
                borderBottom: '1px solid',
                borderColor: 'grey.500',
            }}
        >
            <Box
                key={title}
                sx={{
                    display: 'flex',
                    alingitems: 'flex-start',
                    flexDirection: 'column',
                    padding: '20px 0 0',
                    minHeight: '140px',
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flex: '1',
                        gap: '16px',
                    }}
                >
                    <Box
                        sx={{
                            width: '100px',
                            minHeight: itemLabel ? '150px' : '',
                        }}
                    >
                        <Box
                            onClick={() => openLightbox(thumbnailUrl)}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    div: {
                                        borderColor: '#bababa',
                                    },
                                },
                            }}
                        >
                            <CatalogItemImage
                                src={thumbnailUrl}
                                height="100px"
                                width="100px"
                            />
                        </Box>

                        {itemLabel && (
                            <Typography
                                data-testclass="item-inventory-label"
                                typography="openSans.captionMedium"
                                color="primary.main"
                                align="center"
                                sx={{
                                    marginTop: '16px',
                                }}
                            >
                                {itemLabel}
                            </Typography>
                        )}
                    </Box>
                    <Box flex="1 1">
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'space-between',
                                borderBottom:
                                    showPrice || isGlobalCatalog
                                        ? '1px solid'
                                        : '',
                                borderColor: 'grey.500',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={expandDetailedView(id)}
                            >
                                <CatalogItem
                                    categoryId={
                                        showCategoryLabel ? categoryId : null
                                    }
                                    title={title}
                                    sku={sku}
                                    uom={uom}
                                    qtyPerUom={qtyPerUom}
                                    minAllowedInStock={minAllowedInStock}
                                    label={itemLabel}
                                />
                            </Box>
                            <Box display={!showCheckbox ? 'none' : 'flex'}>
                                <Box height="100%">
                                    <CatalogItemCheckbox
                                        showQtyCheckbox={isMoveToOrderMode}
                                        isActive={isActive}
                                        qty={qty}
                                        itemIdToEdit={idToEdit}
                                        itemId={id}
                                        itemUid={uid}
                                        quickEdit={quickEdit}
                                        isInOrder={isInOrder}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            {showPrice && (
                                <CatalogItemPrice
                                    price={price}
                                    total={totalPrice}
                                    uom={uom}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
                {isGlobalCatalog && (
                    <Accordion
                        sx={{
                            width: '100%',
                            boxShadow: 'none',
                            padding: '4px 0 0 0',
                        }}
                    >
                        <AccordionSummary
                            sx={{ padding: 0, margin: 0 }}
                            expandIcon={<ArrowDropDown color="primary" />}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    sx={{ color: '#000000D9' }}
                                    typography="openSans.body2"
                                >
                                    {t('Suppliers ')} ({proposals?.length || 0})
                                </Typography>
                                <Typography
                                    typography="openSans.button"
                                    color="primary"
                                >
                                    {t('View')}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <SuppliersList
                                proposals={proposals}
                                activatedAccounts={activatedAccounts}
                            />
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        </Box>
    );
};
